import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  postingLogApi,
  NO_ERP_INTEGRATION_MESSAGE,
  type CostingIntegrationStatus,
  type PagedCostingTransactionLogResponse,
} from '@/services/postingLogApi';
import { getApiErrorStatus } from '@/services/reportPaging';
import type { PostingLogQuery, SortDir, TimesheetSortKey } from '@/utils/PostingLog/types';
import { groupPostingLogRows, mapPostingLogRow } from '@/utils/PostingLog/mappers';
import { useSettings } from '@/contexts/SettingsContext';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { getMockPostingLogData } from '@/data/mockPostingLogData';

interface UsePostingLogDataParams {
  canView: boolean;
  query: PostingLogQuery;
  page: number;
  pageSize: number;
  sortKey: TimesheetSortKey;
  sortDir: SortDir;
  matchesNothing: boolean;
  companyId: string | undefined;
}

export function usePostingLogData({ canView, query, page, pageSize, sortKey, sortDir, matchesNothing, companyId }: UsePostingLogDataParams) {
  const { weekStartDay } = useSettings();
  const { isDemo } = useScreenDataMode('reports-posting-log');
  const [result, setResult] = useState<PagedCostingTransactionLogResponse | null>(null);
  const [summary, setSummary] = useState<Pick<PagedCostingTransactionLogResponse, 'totalCount' | 'postedCount' | 'failedCount' | 'timesheetCount'> | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryAccessDenied, setSummaryAccessDenied] = useState(false);
  const [integration, setIntegration] = useState<CostingIntegrationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setSummary(null);
    setSummaryError(null);
    setSummaryAccessDenied(false);
    if (!canView) return;

    let cancelled = false;
    const request = isDemo
      ? Promise.resolve(getMockPostingLogData())
      : postingLogApi.getPostingLog({ pageNumber: 1, pageSize: 1 });

    request
      .then(({ totalCount, postedCount, failedCount, timesheetCount }) => {
        if (!cancelled) setSummary({ totalCount, postedCount, failedCount, timesheetCount });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (getApiErrorStatus(err) === 403) setSummaryAccessDenied(true);
        else setSummaryError(err instanceof Error
          ? `Failed to load the posting log summary: ${err.message}`
          : 'Failed to load the posting log summary');
      });
    return () => { cancelled = true; };
  }, [canView, companyId, isDemo, refreshKey]);

  useEffect(() => {
    if (!canView || matchesNothing) {
      setIsLoading(false);
      setResult(null);
      return;
    }

    if (isDemo) {
      const response = getMockPostingLogData(query);
      setResult(response);
      setIntegration(response.integration);
      setAccessDenied(false);
      setLoadError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    setResult(null);
    let firstPage: PagedCostingTransactionLogResponse | undefined;
    postingLogApi
      .getAllPostingLogRows(query, (response) => { firstPage = response; })
      .then((items) => {
        if (cancelled || !firstPage) return;
        setResult({ ...firstPage, items });
        setIntegration(firstPage.integration ?? null);
        setAccessDenied(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (getApiErrorStatus(err) === 403) setAccessDenied(true);
        else setLoadError(err instanceof Error ? err.message : 'Failed to load the posting log');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [canView, query, matchesNothing, refreshKey, companyId, isDemo]);

  const timesheets = useMemo(() => {
    const grouped = groupPostingLogRows(matchesNothing ? [] : (result?.items ?? []).map(mapPostingLogRow), weekStartDay);
    return grouped.sort((a, b) => {
      const comparison = sortKey === 'weekEnding'
        ? (a.weekEnding?.getTime() ?? 0) - (b.weekEnding?.getTime() ?? 0)
        : a[sortKey].localeCompare(b[sortKey], 'en-GB', { numeric: true });
      return (sortDir === 'asc' ? comparison : -comparison)
        || a.timesheetId.localeCompare(b.timesheetId, 'en-GB', { numeric: true });
    });
  }, [result, matchesNothing, weekStartDay, sortKey, sortDir]);
  const totalCount = timesheets.length;
  const currentPage = Math.min(page, Math.max(1, Math.ceil(totalCount / pageSize)));
  const rows = timesheets.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const integrationConfigured = integration?.isConfigured !== false;
  const retryDisabledReason = integrationConfigured ? undefined : NO_ERP_INTEGRATION_MESSAGE;
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  return {
    result, summary, summaryError, rows, totalCount, currentPage, isLoading, loadError,
    accessDenied: accessDenied || summaryAccessDenied, refreshKey, refresh,
    integrationConfigured, retryDisabledReason,
  };
}
